import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TtsStatusRow } from 'src/app/services/text-to-speech.service';
import { TtsStatusService } from '../tts-status.service';

interface ChipTheme {
  label: string;
  dot: string;
  bg: string;
  border: string;
  text: string;
}

const THEMES: Record<string, ChipTheme> = {
  ready: { label: 'Ready', dot: '#2f9e5b', bg: '#eefaf1', border: '#c9ecd4', text: '#1f7a44' },
  pending: { label: 'Generating', dot: '#d99a1f', bg: '#fdf6e6', border: '#f2dfa8', text: '#a97314' },
  failed: { label: 'Failed', dot: '#d64530', bg: '#fdecea', border: '#f3b9b0', text: '#a5301f' },
  none: { label: 'Not generated', dot: '#9aa1ab', bg: '#f3f4f6', border: '#dde1e7', text: '#5f6773' },
};

// Renders nothing until status has loaded, so a failing status call hides the
// chips instead of marking every field as not generated.
@Component({
  selector: 'tts-status-chip',
  templateUrl: './tts-status-chip.component.html',
  styleUrls: ['./tts-status-chip.component.scss'],
  standalone: true,
  imports: [CommonModule],
})
export class TtsStatusChipComponent {
  @Input() fieldPath: string | null = null;

  @Output() open = new EventEmitter<string>();

  constructor(private ttsStatus: TtsStatusService) {}

  get row(): TtsStatusRow | null {
    return this.fieldPath ? this.ttsStatus.get(this.fieldPath) : null;
  }

  get theme(): ChipTheme | null {
    if (!this.fieldPath) {
      return null;
    }
    const row = this.row;
    if (row) {
      return THEMES[row.status] ?? null;
    }
    return this.ttsStatus.loaded ? THEMES['none'] : null;
  }

  get tooltip(): string {
    const row = this.row;
    if (!row) {
      return 'No audio generated yet';
    }
    const when = this.relativeTime(row.updatedAt);
    if (row.status === 'ready') {
      return `Generated ${when}`;
    }
    if (row.status === 'failed') {
      return `Last tried ${when}`;
    }
    return 'Generating audio';
  }

  onClick(): void {
    if (this.fieldPath && this.theme) {
      this.open.emit(this.fieldPath);
    }
  }

  private relativeTime(iso: string): string {
    const then = new Date(iso).getTime();
    if (isNaN(then)) {
      return 'recently';
    }

    const minutes = Math.floor((Date.now() - then) / 60000);
    if (minutes < 1) {
      return 'just now';
    }
    if (minutes < 60) {
      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    }

    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }
}
